import { getFormProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form, useFetcher, useSearchParams } from '@remix-run/react'
import { useEffect, useRef } from 'react'
import { LuCheck } from 'react-icons/lu'
import { SiInstagram, SiLinkedin } from 'react-icons/si'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { SOCIALS } from '#app/config/constants.tsx'
import newsLetterBackground from '#app/images/home_newsletter.jpg'
import newsLetterOverlay from '#app/images/newsletter_overlay.png'
import { type ActionData } from '#app/newsletter/types.js'
import { EmailSchema, NameSchema } from '#app/utils/user-validation'
// import { sanitizeInput } from '#app/utils/util.ts'
import { Field } from './forms'
import IconLink from './iconLink'
import { H2, H3, Paragraph } from './typography'
import { Button } from './ui/button'

const NewsLetterSchema = z.object({
	name: NameSchema,
	email: EmailSchema,
	redirectTo: z.string().optional(),
	verified: z.boolean().optional(),
})

export default function NewsLetterSignup({
	newsLetterBackgroundImage = newsLetterBackground,
}) {
	const convertkit = useFetcher<ActionData>()
	const formRef = useRef<HTMLFormElement>(null)
	const isDone = convertkit.state === 'idle' && convertkit.data != null
	const convertkitData = isDone ? convertkit.data : null

	useEffect(() => {
		if (formRef.current && convertkitData?.status === 'success') {
			formRef.current.reset()
		}
	}, [convertkitData])

	const [form, fields] = useForm({
		id: 'newsletter-signup-comp',
		constraint: getZodConstraint(NewsLetterSchema),
		// defaultValue: { redirectTo },
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: NewsLetterSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	const success = isDone && convertkitData?.status === 'success'

	return (
		<section className="mx-auto mt-10">
			<div className="relative flex min-h-[650px] items-center justify-center overflow-hidden lg:aspect-[16/9]">
				<img
					src={newsLetterBackgroundImage}
					className="absolute inset-0 h-full w-full"
				/>
				<div className="absolute flex w-11/12 -translate-y-1/3 flex-col items-center gap-4 sm:w-1/2 lg:w-1/3">
					<convertkit.Form
						ref={formRef}
						method="POST"
						action="/newsletter"
						className="flex w-full flex-col gap-4"
						{...getFormProps(form)}
					>
						<HoneypotInputs />
						<div className="flex flex-col items-center justify-center">
							<H2 className="shadow-3xl">Join my Newsletter</H2>
							<H3 className="shadow-3xl">To Never Miss a Sip</H3>
						</div>
						<input type="hidden" name="formId" value="newsletter" />
						<input type="hidden" name="convertKitTagId" value={''} />
						<input type="hidden" name="convertKitFormId" value={'7669945'} />
						<Field
							name="name"
							label="Naam"
							placeholder="Your name"
							className="text-black"
							errors={fields.name.errors}
						/>
						<Field
							name="email"
							label="Email"
							type="email"
							placeholder="Email"
							className="text-black"
							errors={fields.email.errors}
						/>
						{success ? (
							<div className="flex items-center justify-center gap-4">
								<LuCheck size={'24px'} />
								<Paragraph className="font-bold">
									Success! Check you're email (and spam) to confirm you're
									sign-up.
								</Paragraph>
							</div>
						) : (
							<Button className="w-full shadow-3xl hover:bg-tertiary hover:text-white">
								Sign up!
							</Button>
						)}
					</convertkit.Form>
					<div className="mt-4 flex gap-8">
						<IconLink target={SOCIALS.instagram}>
							<SiInstagram size={'24px'} color="white" />
						</IconLink>
						<IconLink target={SOCIALS.linkedin}>
							<SiLinkedin size={'24px'} color="white" />
						</IconLink>
					</div>
				</div>
				<img
					src={newsLetterOverlay}
					className="absolute bottom-0 z-10 w-full object-center"
				/>
			</div>
		</section>
	)
}
